import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Row, Col } from "react-bootstrap";
import { Event } from "../../../types/model";
import { FaCalendar } from "react-icons/fa6";
import { IoIosTrendingDown } from "react-icons/io";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import { setShowLoginModal } from "../../../store/slices/userSlice";
import {
	setSelectedEvent,
} from "../../../store/slices/fantasySlice";
import { FaHandPointRight } from "react-icons/fa";
import { getFutureEvents } from "../../../utils/helper";
import { formatEventDate } from "../../../utils/eventDateTime";

interface NextEventCardProps {
	event: Event;
}

const NextEventCard: React.FC<NextEventCardProps> = ({ event }) => {
	const { isLoggedIn, isMobile } = useAppSelector(
		(state: RootState) => state.user,
	);
	const { events } = useAppSelector((state: RootState) => state.fantasy);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const handlePickRiders = () => {
		if (!isLoggedIn) {
			dispatch(setShowLoginModal(true));
			return;
		}

		dispatch(setSelectedEvent(event));
		navigate(`/editteam?sport=${event.sport._id}&series=${event.series._id}`);
	};

	const [timeRemaining, setTimeRemaining] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	});

	const nextEventInSportAndSeries = useMemo(() => {
		const timeline = getFutureEvents(events).filter(
			(timelineEvent) =>
				timelineEvent.sport?._id === event.sport?._id &&
				timelineEvent.series?._id === event.series?._id,
		);

		const currentIndex = timeline.findIndex(
			(timelineEvent) => timelineEvent._id === event._id,
		);

		if (currentIndex >= 0 && currentIndex < timeline.length - 1) {
			return timeline[currentIndex + 1];
		}

		return null;
	}, [event._id, event.series?._id, event.sport?._id, events]);

	const handleNextRace = () => {
		if (!nextEventInSportAndSeries) {
			return;
		}

		dispatch(setSelectedEvent(nextEventInSportAndSeries));
	};

	useEffect(() => {
		const calculateTimeRemaining = () => {
			const eventDate = new Date(event.date).getTime();
			const now = new Date().getTime();
			const difference = eventDate - now;

			if (difference > 0) {
				setTimeRemaining({
					days: Math.floor(difference / (1000 * 60 * 60 * 24)),
					hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
					minutes: Math.floor((difference / (1000 * 60)) % 60),
					seconds: Math.floor((difference / 1000) % 60),
				});
			} else {
				setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
			}
		};

		const timer = setInterval(calculateTimeRemaining, 1000);
		return () => clearInterval(timer);
	}, [event.date]);

	return (
		<Card
			className="text-center py-4"
			style={{
				backgroundColor: "transparent",
				border: "none",
			}}
		>
			<Card.Body className="text-md-start text-center">
				<Button
					variant="success"
					size="sm"
					className="mb-3 py-0 px-3 rounded-pill fw-bold text-light"
					onClick={handleNextRace}
					disabled={!nextEventInSportAndSeries}
				>
					Next Race
				</Button>
				<div
					className="next-event-card__meta d-flex flex-row justify-content-md-start justify-content-center align-items-center w-100 mb-0"
					style={{ fontSize: "0.8rem" }}
				>
					<FaCalendar className="me-1" />
					{formatEventDate(event.date)}
					<IoIosTrendingDown className="ms-3 me-1" />
					{event.sport.name}
				</div>
				<div
					style={{
						fontSize: isMobile ? "2.9rem" : "4.5rem",
						fontWeight: "bold",
						lineHeight: "1",
					}}
					className="next-event-card__series mb-0 text-md-start text-center"
				>
					{event.series.name}
				</div>
				<div
					style={{
						fontSize: isMobile ? "2.9rem" : "4.5rem",
						fontWeight: "bold",
						lineHeight: "1",
					}}
					className="text-white mt-0 text-md-start text-center"
				>
					{event.location}
				</div>
				<Row className="justify-content-md-start justify-content-center mb-4">
					{["Days", "Hours", "Mins", "Secs"].map((label, index) => {
						const value = [
							timeRemaining.days,
							timeRemaining.hours,
							timeRemaining.minutes,
							timeRemaining.seconds,
						][index];
						return (
							<Col key={label} className="text-center mx-2 p-0" xs="auto">
								<div
									style={{ fontSize: "0.8rem" }}
									className="next-event-card__countdown-label mb-0 pb-0"
								>
									{label}
								</div>
								<div
									style={{ fontSize: "1.7rem" }}
									className="next-event-card__countdown-value fw-bold"
								>
									{value}
								</div>
							</Col>
						);
					})}
				</Row>
				{isLoggedIn ? (
					<Button
						variant="primary"
						size="lg"
						onClick={handlePickRiders}
						className="fw-bold text-light px-4"
					>
						PICK YOUR RIDERS
					</Button>
				) : (
					<Button
						variant="primary"
						size="sm"
						onClick={handlePickRiders}
						className="fw-bold text-light fs-6 px-3"
					>
						Sign Up and Pick Your Riders
					</Button>
				)}
			</Card.Body>
		</Card>
	);
};

export default NextEventCard;
